import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { RewardModalProps } from "./types";
import {
  formatRewardValue,
  getRarityBadgeClass,
  getRarityGlow,
  getRewardTypeLabel,
} from "./utils";

export function RewardModal({
  open,
  onOpenChange,
  reward,
  onPlayAgain,
}: RewardModalProps) {
  if (!reward) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-purple-500/30 bg-gradient-to-b from-[#0f172a]/95 to-[#020617]/95 text-white backdrop-blur-xl sm:max-w-md">
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl">
            <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
              🎉 You Won!
            </span>
          </DialogTitle>
          <DialogDescription className="text-purple-200/80">
            Congratulations! You won a {getRewardTypeLabel(reward).toLowerCase()}{" "}
            reward.
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "mx-auto flex flex-col items-center rounded-2xl border border-white/10 bg-white/5 p-8",
            getRarityGlow(reward.rarity)
          )}
        >
          <motionSparkles />
          <span className="text-6xl" role="img" aria-hidden>
            {reward.icon}
          </span>
          <p className="mt-4 text-2xl font-bold">{formatRewardValue(reward)}</p>
          <p className="mt-1 text-sm text-purple-200/70">{reward.label}</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-200">
              {getRewardTypeLabel(reward)}
            </span>
            <span
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider",
                getRarityBadgeClass(reward.rarity)
              )}
            >
              {reward.rarity}
            </span>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            onClick={onPlayAgain}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-purple-950 hover:from-amber-400 hover:to-amber-500"
          >
            Play Again
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full border-purple-500/30 text-purple-100 hover:bg-purple-500/10"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function motionSparkles() {
  return (
    <div className="mb-2 flex items-center gap-1 text-amber-400">
      <Sparkles className="h-4 w-4" />
      <Sparkles className="h-5 w-5" />
      <Sparkles className="h-4 w-4" />
    </div>
  );
}
